/**
 * Super Admin — Review-Ban Appeals inbox.
 *
 * Wires to /api/superadmin/appeals + POST /api/superadmin/appeals/{id}/decide.
 * Approving lifts the ban AND re-approves the user's auto-rejected reviews
 * in one shot (backend handles the cascade).
 */
import React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Gavel, CheckCircle2, XCircle, Loader2, Filter, Mail, Clock, Send,
} from "lucide-react";
import { toast } from "sonner";

interface Appeal {
  appeal_id: string;
  user_id: string;
  user_email: string;
  user_name?: string | null;
  reason: string;
  status: "pending" | "resolved" | "denied";
  created_at: string;
  updated_at?: string;
  decision_note?: string | null;
  decided_by?: string | null;
  decided_at?: string | null;
}

type Status = "pending" | "resolved" | "denied";
const STATUS_LABELS: Record<Status, string> = {
  pending: "Pending",
  resolved: "Approved",
  denied: "Denied",
};

export default function SuperAdminAppeals() {
  const [status, setStatus] = React.useState<Status>("pending");
  const [notes, setNotes] = React.useState<Record<string, string>>({});
  const qc = useQueryClient();

  const q = useQuery<{ items: Appeal[] }>({
    queryKey: ["sa-appeals", status],
    queryFn: async () => (await api.get(`/superadmin/appeals?status=${status}`)).data,
    refetchInterval: 20_000,
  });

  const decide = useMutation({
    mutationFn: async ({ id, decision, note }: { id: string; decision: "approve" | "deny"; note?: string }) => {
      const r = await api.post(`/superadmin/appeals/${id}/decide`, { decision, note });
      return r.data;
    },
    onSuccess: (_d, vars) => {
      toast.success(
        vars.decision === "approve"
          ? "Approved — ban lifted and prior reviews restored."
          : "Denied — user notified."
      );
      qc.invalidateQueries({ queryKey: ["sa-appeals"] });
    },
    onError: (err: any) => toast.error(err?.response?.data?.detail || "Decision failed"),
  });

  return (
    <DashboardLayout
      title="Review-Ban Appeals"
      subtitle="Approve to lift the ban and restore the reviewer's history · Deny to keep the pause active."
      seoNoindex
    >
      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="sa-appeals-filters">
        <Filter className="h-3.5 w-3.5 text-[color:var(--sg-muted)]"/>
        {(Object.keys(STATUS_LABELS) as Status[]).map((s) => (
          <button
            key={s}
            data-testid={`sa-appeals-filter-${s}`}
            onClick={() => setStatus(s)}
            className={`h-8 px-3 rounded-full text-xs font-semibold border transition-colors ${
              status === s
                ? "bg-[color:var(--sb-navy,#0A1128)] text-white border-transparent"
                : "border-[color:var(--sg-border)] text-[color:var(--sg-muted)] hover:bg-[color:var(--sg-muted-bg)]"
            }`}
          >
            {STATUS_LABELS[s]}
          </button>
        ))}
        <div className="ml-auto text-[10px] uppercase tracking-widest text-[color:var(--sg-muted)]">
          {q.data?.items?.length ?? 0} in this bucket · refreshes every 20s
        </div>
      </div>

      {q.isLoading ? (
        <div className="flex items-center justify-center py-16 text-[color:var(--sg-muted)]" data-testid="sa-appeals-loading">
          <Loader2 className="h-5 w-5 animate-spin mr-2"/> Loading appeals…
        </div>
      ) : (q.data?.items?.length ?? 0) === 0 ? (
        <div className="rounded-2xl border border-dashed border-[color:var(--sg-border)] p-10 text-center text-[color:var(--sg-muted)]" data-testid="sa-appeals-empty">
          <Gavel className="h-8 w-8 mx-auto mb-2 text-emerald-500"/>
          Nothing in the {STATUS_LABELS[status].toLowerCase()} bucket. You're clear.
        </div>
      ) : (
        <div className="space-y-3" data-testid="sa-appeals-list">
          {q.data!.items.map((a) => {
            const note = notes[a.appeal_id] || "";
            const busy = decide.isPending && decide.variables?.id === a.appeal_id;
            return (
              <div
                key={a.appeal_id}
                data-testid={`sa-appeal-card-${a.appeal_id}`}
                className="rounded-2xl border border-[color:var(--sg-border)] bg-[color:var(--sg-card)] p-5"
              >
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-[color:var(--sg-muted-bg)] text-[color:var(--sg-fg)]">
                        {a.appeal_id}
                      </span>
                      <span className="text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200">
                        {a.status}
                      </span>
                      <span className="text-[11px] text-[color:var(--sg-muted)] flex items-center gap-1">
                        <Clock className="h-3 w-3"/> Filed {new Date(a.created_at).toLocaleString()}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 mt-1">
                      <Mail className="h-3.5 w-3.5 text-[color:var(--sg-muted)]"/>
                      <span className="font-serif-display text-lg font-bold text-[color:var(--sg-fg)]">
                        {a.user_name || a.user_email.split("@")[0]}
                      </span>
                      <span className="text-xs text-[color:var(--sg-muted)]">{a.user_email}</span>
                    </div>
                    <p className="mt-2 text-sm text-[color:var(--sg-fg)] leading-relaxed whitespace-pre-line">
                      {a.reason}
                    </p>

                    {a.decision_note && (
                      <div className="mt-2 rounded-lg bg-slate-500/10 border border-slate-500/30 px-3 py-2 text-[11px] text-[color:var(--sg-muted)]">
                        <b>Prior decision note:</b> {a.decision_note}
                      </div>
                    )}
                  </div>
                </div>

                {a.status === "pending" && (
                  <div className="mt-4 grid grid-cols-1 md:grid-cols-[1fr_auto] gap-2">
                    <Textarea
                      data-testid={`sa-appeal-note-${a.appeal_id}`}
                      value={note}
                      onChange={(e) => setNotes((n) => ({ ...n, [a.appeal_id]: e.target.value }))}
                      placeholder="Optional note to the reviewer (shows on their next PDP visit)…"
                      rows={2}
                      className="text-sm bg-white dark:bg-slate-900/40"
                    />
                    <div className="flex md:flex-col gap-2">
                      <Button
                        data-testid={`sa-appeal-approve-${a.appeal_id}`}
                        className="h-9 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold"
                        disabled={busy}
                        onClick={() => decide.mutate({ id: a.appeal_id, decision: "approve", note })}
                      >
                        {busy && decide.variables?.decision === "approve"
                          ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin"/> Approving…</>
                          : <><CheckCircle2 className="h-4 w-4 mr-1.5"/> Approve · lift ban</>}
                      </Button>
                      <Button
                        data-testid={`sa-appeal-deny-${a.appeal_id}`}
                        variant="outline"
                        className="h-9 border-rose-300 text-rose-700 hover:bg-rose-50 dark:border-rose-800 dark:text-rose-300 dark:hover:bg-rose-900/30 font-semibold"
                        disabled={busy}
                        onClick={() => decide.mutate({ id: a.appeal_id, decision: "deny", note })}
                      >
                        {busy && decide.variables?.decision === "deny"
                          ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin"/> Denying…</>
                          : <><XCircle className="h-4 w-4 mr-1.5"/> Deny</>}
                      </Button>
                    </div>
                  </div>
                )}

                {a.status !== "pending" && (
                  <div className="mt-3 text-[11px] text-[color:var(--sg-muted)] flex items-center gap-2">
                    <Send className="h-3 w-3"/>
                    Decided {a.decided_at ? new Date(a.decided_at).toLocaleString() : "—"} by {a.decided_by || "—"}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </DashboardLayout>
  );
}
